/**
 * CashDrawerCard — manager-facing setup tile for the till.
 *
 * Props:
 *   compact (default false): when true, renders a single-line strip on
 *     paired state instead of the full card (used on the Live Orders page
 *     where we want it discrete unless action is needed).
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Banknote, AlertTriangle, CheckCircle2, Plug, Trash2 } from 'lucide-react';
import {
  isCashDrawerSupported,
  isCashDrawerPaired,
  pairCashDrawer,
  forgetCashDrawer,
  openCashDrawer,
} from '../lib/cashDrawer';

export default function CashDrawerCard({ compact = false }) {
  const supported = isCashDrawerSupported();
  const [paired, setPaired] = useState(isCashDrawerPaired());
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    // Re-read once on mount (in case the marker changed in another tab).
    setPaired(isCashDrawerPaired());
  }, []);

  const onPair = async () => {
    setBusy('pair');
    try {
      const { name } = await pairCashDrawer();
      setPaired(true);
      toast.success(`Cash drawer paired: ${name}`);
    } catch (e) {
      toast.error(e.message || 'Pairing cancelled');
    } finally { setBusy(null); }
  };

  const onForget = async () => {
    setBusy('forget');
    try {
      await forgetCashDrawer();
      setPaired(false);
      toast.success('Cash drawer unpaired');
    } finally { setBusy(null); }
  };

  const onTest = async () => {
    setBusy('test');
    try {
      await openCashDrawer();
      toast.success('Drawer opened — you should hear a click');
    } catch (e) {
      toast.error(e.message || 'Drawer kick failed');
    } finally { setBusy(null); }
  };

  // Hide entirely on Orders page (compact mode) once paired — the manager
  // doesn't need to see the card every time, just when there's nothing
  // paired yet.
  if (compact && paired && supported) return null;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-sm p-4 space-y-3" data-testid="cash-drawer-card">
      <div className="flex items-center gap-2">
        <Banknote className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-sans text-white uppercase tracking-wider font-semibold">Cash drawer (till)</h3>
        {paired && supported && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-300 font-sans">
            <CheckCircle2 className="w-3 h-3" /> Paired
          </span>
        )}
      </div>
      {!supported ? (
        <div className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 font-sans flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            This browser doesn't support WebUSB so the till can't open automatically.
            Please use <strong>Chrome</strong> or <strong>Edge</strong> on the till
            computer and reload this page to pair the cash drawer.
          </p>
        </div>
      ) : !paired ? (
        <div className="space-y-3">
          <p className="text-zinc-400 text-xs font-sans leading-relaxed">
            One-time setup. Click below, then pick your <strong>thermal receipt printer</strong>
            (e.g. <em>WG8200</em>) from the browser's USB picker. The drawer plugs into the
            printer's RJ11/RJ12 "DK" port and pops every time staff settles a cash sale.
          </p>
          <Button
            onClick={onPair}
            disabled={busy === 'pair'}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm font-sans"
            data-testid="pair-cash-drawer"
          >
            <Plug className="w-4 h-4 mr-2" /> {busy === 'pair' ? 'Waiting for picker…' : 'Pair cash drawer'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-zinc-400 text-xs font-sans leading-relaxed">
            Drawer paired on this device. The till will pop automatically when staff
            taps <strong>Cash · No Discount / Cash 10% / Friends 20%</strong>. Use
            "Test open" before service to confirm the kick works.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={onTest}
              disabled={busy === 'test'}
              className="flex-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 rounded-sm font-sans text-xs"
              data-testid="test-cash-drawer"
            >
              <Banknote className="w-3.5 h-3.5 mr-1" /> {busy === 'test' ? 'Sending…' : 'Test open'}
            </Button>
            <Button
              onClick={onForget}
              disabled={busy === 'forget'}
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs"
              data-testid="forget-cash-drawer"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Forget
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
